import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { api } from '../lib/api';
import { FloatingActions } from './Home';
import useSeo, { faqLd } from '../hooks/useSeo';

const InsightDetailLive = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useSeo({
    title: item ? item.title : 'Article',
    description: item ? (item.excerpt || item.summary || 'Engineering insights from Land Drafters.') : 'Engineering insights from Land Drafters.',
    path: `/insights/${slug}`,
    type: 'article',
    image: item ? item.image : undefined,
    keywords: item?.keywords?.length ? item.keywords : undefined,
    jsonLd: item?.faqs?.length ? [faqLd(item.faqs)] : undefined
  });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.insight(slug)
      .then(d => { if (alive) setItem(d); })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-500">
        <Icons.Loader2 className="w-6 h-6 animate-spin text-green-600 mr-2" /> Loading…
      </div>
    );
  }
  if (err || !item) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-gray-500 px-6 text-center">
        <Icons.FileQuestion className="w-10 h-10 text-orange-500 mb-3" />
        <p className="font-bold">Article not found.</p>
        <Link to="/insights" className="mt-4 text-green-600 font-bold underline">Back to all insights</Link>
      </div>
    );
  }

  const TYPE_META = {
    news: { backLink: '/news', backLabel: 'All News' },
    blog: { backLink: '/blogs', backLabel: 'All Blogs' },
  };
  const meta = TYPE_META[item.type] || { backLink: '/insights', backLabel: 'All Insights' };
  const { backLink, backLabel } = meta;

  return (
    <>
      <section className="py-20 bg-[#0e1116] text-white">
        <div className="max-w-4xl mx-auto px-6">
          <Link to={backLink} className="inline-flex items-center gap-1 text-green-400 text-xs font-bold uppercase tracking-wider mb-5 hover:text-orange-400"><Icons.ArrowLeft className="w-3.5 h-3.5" /> {backLabel}</Link>
          <div className="inline-flex items-center gap-2 bg-gray-700 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded mb-4">
            {item.type === 'news' ? <Icons.Newspaper className="w-3 h-3" /> : <Icons.BookOpen className="w-3 h-3" />}
            {item.category}
          </div>
          <h1 className="text-4xl md:text-5xl font-black leading-tight">{item.title}</h1>
          <div className="flex items-center gap-4 mt-5 text-xs text-gray-400">
            <span>By Land Drafters Editorial</span><span>·</span><span>{item.date}</span><span>·</span><span>{item.read_time}</span>
            <span>·</span><span className="flex items-center gap-1"><Icons.Eye className="w-3 h-3" /> {item.views} views</span>
          </div>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <img src={item.image} alt="" className="w-full h-[440px] object-cover rounded-lg mb-8" />
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-xl text-gray-600 leading-relaxed">{item.excerpt}</p>
            {item.body?.length ? (
              <div data-testid="article-body">
                {item.body.map((sec, i) => (
                  <div key={i}>
                    {sec.h && <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">{sec.h}</h2>}
                    {sec.p && <p className="mt-4">{sec.p}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <p className="mt-6">In today&apos;s rapidly evolving infrastructure landscape, advanced engineering practices and digital methodologies have become essential drivers of project success. As global markets demand faster delivery, higher quality, and more sustainable solutions, integrated CAD drafting, BIM, GIS, and digital twin technologies are reshaping how complex projects are conceived, designed, and delivered.</p>
                <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">A New Era of Digital Engineering</h2>
                <p>From scan-to-BIM workflows that capture as-built conditions with millimeter accuracy, to digital twins that mirror the operational lifecycle of physical assets, the discipline of engineering has expanded far beyond traditional 2D drawings. Modern engineering teams now operate across an interconnected ecosystem of data, models, and analytics.</p>
                <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">Practical Outcomes for Clients</h2>
                <p>The result for our clients is tangible: reduced rework, faster permit cycles, more accurate construction estimates, and infrastructure assets that perform reliably across decades of operation.</p>
                <blockquote className="border-l-4 border-gray-600 pl-5 my-8 italic text-gray-600">
                  “The future of engineering lies not in any single tool, but in the seamless integration of multiple disciplines, technologies, and human expertise.”
                </blockquote>
                <h2 className="text-2xl font-black mt-10 mb-4 text-[#0e1116]">Looking Ahead</h2>
                <p>Land Drafters continues to expand its capabilities, training its engineers in the latest platforms while maintaining the deep expertise that has earned us the trust of over 350 active client partners.</p>
              </>
            )}
          </div>
          {item.faqs?.length ? (
            <div className="mt-12" data-testid="article-faqs">
              <h2 className="text-2xl font-black text-[#0e1116] mb-5">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {item.faqs.map(([q, a]) => (
                  <div key={q} className="bg-gray-50 border border-gray-200 rounded-lg p-5">
                    <div className="font-bold text-[#0e1116]">{q}</div>
                    <p className="text-gray-600 text-sm mt-2 leading-relaxed">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="mt-12 p-8 bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg text-white">
            <h3 className="text-2xl font-black">Ready to start your next project?</h3>
            <p className="text-green-100 mt-2">Connect with our engineering experts and get a quote in 24 hours.</p>
            <Link to="/get-quote" className="inline-flex items-center gap-2 mt-5 bg-white text-green-700 hover:bg-orange-500 hover:text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider transition-colors">Get a Quote <Icons.ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
      <FloatingActions />
    </>
  );
};

export default InsightDetailLive;
